import { z } from 'zod';
import { waferIdTrainingDatasetManifestSchema, waferIdTrainingLabelSourceSchema, waferIdTrainingManifestRowSchema, } from './wafer-id-training-manifest.schema.js';
export { waferIdTrainingLabelSourceSchema, waferIdTrainingManifestRowSchema, waferIdTrainingDatasetManifestSchema, waferIdTrainingDatasetS3Prefix, waferIdTrainingDatasetManifestS3Key, } from './wafer-id-training-manifest.schema.js';
export const waferIdMlTrainingJobStatusSchema = z.enum([
    'pending',
    'starting',
    'running',
    'completed',
    'failed',
    'stopped',
]);
export const waferIdMlHoldoutEvalResultRowSchema = z.object({
    image_key: z.string(),
    ground_truth: z.string(),
    predicted: z.string(),
    exact_match: z.boolean(),
});
export const waferIdMlHoldoutEvalSummarySchema = z.object({
    holdout_rows: z.number().int().nonnegative(),
    exact_match: z.number().int().nonnegative(),
    exact_match_rate: z.number().min(0).max(1),
    results: z.array(waferIdMlHoldoutEvalResultRowSchema),
});
export const waferIdMlDatasetSummarySchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    version: z.string(),
    s3Prefix: z.string(),
    rowCount: z.number().int().nonnegative(),
    publishedAt: z.string().datetime(),
    sourceCounts: z.record(waferIdTrainingLabelSourceSchema, z.number().int().nonnegative()).optional(),
});
export const waferIdMlDatasetDetailSchema = waferIdMlDatasetSummarySchema.extend({
    manifest: waferIdTrainingDatasetManifestSchema,
});
export const waferIdMlTrainingJobSummarySchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    datasetId: z.string().uuid(),
    datasetVersion: z.string(),
    sagemakerJobName: z.string().nullable(),
    status: waferIdMlTrainingJobStatusSchema,
    holdoutEval: waferIdMlHoldoutEvalSummarySchema.nullable().optional(),
    modelArtifactS3: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable().optional(),
});
export const waferIdMlModelVersionSummarySchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    trainingJobId: z.string().uuid(),
    label: z.string(),
    holdoutExactMatchRate: z.number().min(0).max(1).nullable().optional(),
    promotedAt: z.string().datetime().nullable().optional(),
    isLive: z.boolean(),
    packagedModelS3: z.string().nullable().optional(),
    sagemakerModelName: z.string().nullable().optional(),
});
export const waferIdMlEvalRunSummarySchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    modelVersionId: z.string().uuid(),
    modelVersionLabel: z.string(),
    evalDatasetId: z.string().uuid(),
    evalDatasetVersion: z.string(),
    exactMatchRate: z.number().min(0).max(1).nullable(),
    status: z.enum(['pending', 'running', 'completed', 'failed']),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable().optional(),
});
export const waferIdMlEvalRunResultsSchema = z.object({
    rows: z.number().int().nonnegative(),
    exact_match: z.number().int().nonnegative(),
    exact_match_rate: z.number().min(0).max(1),
    results: z.array(waferIdMlHoldoutEvalResultRowSchema),
    model_type: z.string().optional(),
});
export const waferIdMlEvalRunDetailSchema = waferIdMlEvalRunSummarySchema.extend({
    results: waferIdMlEvalRunResultsSchema.nullable().optional(),
    sagemakerConsoleUrl: z.string().url().nullable().optional(),
});
export const waferIdMlEndpointStatusSchema = z.object({
    endpointName: z.string(),
    status: z.string(),
    endpointConfigName: z.string().nullable(),
    modelType: z.string().nullable().optional(),
});
export const waferIdMlDatasetListResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        datasets: z.array(waferIdMlDatasetSummarySchema),
    }),
});
export const waferIdMlDatasetDetailResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlDatasetDetailSchema,
});
export const waferIdMlTrainingJobListResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        jobs: z.array(waferIdMlTrainingJobSummarySchema),
    }),
});
export const waferIdMlEndpointStatusResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlEndpointStatusSchema,
});
export const waferIdMlStartTrainingJobRequestSchema = z.object({
    datasetId: z.string().uuid(),
    preset: z.enum(['quick', 'standard', 'custom']).default('standard'),
    epochs: z.number().int().min(1).max(50).optional(),
    batchSize: z.number().int().min(1).max(32).optional(),
    learningRate: z.number().positive().optional(),
});
export const waferIdMlStartEvalRunRequestSchema = z.object({
    modelVersionId: z.string().uuid(),
    evalDatasetId: z.string().uuid(),
});
export const waferIdMlPublishDatasetRowSchema = waferIdTrainingManifestRowSchema.omit({
    tenant_id: true,
    image_local_path: true,
});
export const waferIdMlPublishDatasetRequestSchema = z.object({
    version: z.string().min(1).max(64),
    rows: z.array(waferIdMlPublishDatasetRowSchema).min(1),
});
export const waferIdMlPublishDatasetResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlDatasetSummarySchema,
});
function isBrowserFile(value) {
    const FileCtor = globalThis.File;
    return typeof FileCtor === 'function' && value instanceof FileCtor;
}
export const waferIdMlPublishDatasetUploadFormSchema = z.object({
    version: z.string().min(1).max(64),
    labels: z.custom(isBrowserFile),
    images: z.custom(isBrowserFile).optional(),
});
export const waferIdMlTrainingJobDetailSchema = waferIdMlTrainingJobSummarySchema.extend({
    hyperparams: z.record(z.unknown()).nullable().optional(),
    sagemakerConsoleUrl: z.string().url().nullable().optional(),
});
export const waferIdMlTrainingJobDetailResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlTrainingJobDetailSchema,
});
export const waferIdMlStartTrainingJobResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlTrainingJobSummarySchema,
});
export const waferIdMlModelVersionListResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        modelVersions: z.array(waferIdMlModelVersionSummarySchema),
    }),
});
export const waferIdMlPromoteModelVersionResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlModelVersionSummarySchema,
});
export const waferIdMlEvalRunListResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        evalRuns: z.array(waferIdMlEvalRunSummarySchema),
    }),
});
export const waferIdMlEvalRunDetailResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlEvalRunDetailSchema,
});
export const waferIdMlStartEvalRunResponseSchema = z.object({
    success: z.literal(true),
    data: waferIdMlEvalRunSummarySchema,
});
export const waferIdMlTenantParamsSchema = z.object({
    tenantId: z.string().uuid(),
});
export const waferIdMlTrainingJobParamsSchema = z.object({
    tenantId: z.string().uuid(),
    jobId: z.string().uuid(),
});
export const waferIdMlModelVersionParamsSchema = z.object({
    tenantId: z.string().uuid(),
    modelVersionId: z.string().uuid(),
});
export const waferIdMlEvalRunParamsSchema = z.object({
    tenantId: z.string().uuid(),
    evalRunId: z.string().uuid(),
});
//# sourceMappingURL=wafer-id-ml.schemas.js.map